/**
 * «Vacaciones» on a person's card (the owner, 2026-10-31: «a button on the payroll page that allows us
 * to log used vacation days outside of payroll»): the days somebody rested, written down when they
 * happen (`empleado.vacaciones`). The run of those days pays their salary as always, and what was set
 * aside for the vacation pays it, as with the days typed on a run's line (`vacacionesDescansadas`):
 * nothing is handed over here.
 */
import { useState } from 'react';
import { diasEntre, formatMoney, formatQuantity, hoy, newId, parseQuantity, type Milli } from '@abastece/shared';
import { vacacionesDescansadas, type Empleado } from '@abastece/core';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import { Campo } from './Campo.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

const fecha = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
/** Thousandths of a day as the sheet says them: «2.5». */
const dias = (milesimas: number) => formatQuantity(milesimas as Milli);

export function HojaVacaciones({ empleado, onCerrar }: { empleado: Empleado; onCerrar: () => void }) {
  const t = useT();
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  /** The days as typed; until then, every day from the first to the last. */
  const [diasEscritos, setDiasEscritos] = useState<string | null>(null);
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const fechasBien = fecha(desde) && fecha(hasta) && desde <= hasta;
  const enElRango = fechasBien ? diasEntre(desde, hasta) + 1 : 0;
  const texto = diasEscritos ?? (fechasBien ? String(enElRango) : '');
  const milesimas = texto.trim() === '' ? null : parseQuantity(texto);
  const diasBien = fechasBien && milesimas !== null && 0 < milesimas && milesimas <= enElRango * 1000;
  const antesDeEntrar = fecha(desde) && desde < empleado.fechaIngreso;
  const lista = diasBien && !antesDeEntrar;
  // What the books will do: the days off what was earned, so much a day off what was set aside.
  const efecto = diasBien ? vacacionesDescansadas(empleado, milesimas) : null;

  async function registrar() {
    if (!lista || milesimas === null) return;
    setError(null);
    setTrabajando(true);
    try {
      const { rechazo } = await emitirYVer('empleado.vacaciones', {
        vacacionesId: newId(),
        empleadoId: empleado.id,
        desde,
        hasta,
        dias: milesimas,
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={t('vacaciones.titulo', { nombre: empleado.nombre })}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          {error && <p className="text-sm text-rose-400">{error}</p>}
          <button type="button" className="btn-primario" disabled={trabajando || !lista} onClick={() => void registrar()}>
            <Ico icono="vacaciones" />
            {t('vacaciones.registrar')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-hoja-vacaciones>
        <p className="text-sm text-slate-300" data-vacaciones-tiene>
          {t('vacaciones.tiene', { dias: dias(empleado.diasVacacionesAcumulados), monto: formatMoney(empleado.vacacionesAcumuladas) })}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Campo
            etiqueta={t('vacaciones.desde')}
            type="date"
            value={desde}
            min={empleado.fechaIngreso}
            onChange={(e) => {
              setDesde(e.target.value);
              // The last day keeps up with the first: never before it.
              if (fecha(e.target.value) && e.target.value > hasta) setHasta(e.target.value);
            }}
            error={antesDeEntrar ? t('vacaciones.antesDeEntrar', { fecha: empleado.fechaIngreso }) : null}
          />
          <Campo
            etiqueta={t('vacaciones.hasta')}
            type="date"
            value={hasta}
            min={desde}
            onChange={(e) => setHasta(e.target.value)}
            error={fecha(desde) && fecha(hasta) && hasta < desde ? t('vacaciones.hastaAntes') : null}
          />
        </div>
        <Campo
          etiqueta={t('vacaciones.dias')}
          inputMode="decimal"
          className="tabular"
          value={texto}
          onChange={(e) => setDiasEscritos(e.target.value)}
          autoComplete="off"
          data-vacaciones-dias
          error={fechasBien && texto.trim() !== '' && !diasBien ? t('vacaciones.diasFuera', { n: enElRango }) : null}
          ayuda={fechasBien ? t('vacaciones.diasAyuda', { n: enElRango }) : undefined}
        />
        {efecto && (
          <p className="text-sm text-slate-300" data-vacaciones-efecto>
            {t('vacaciones.efecto', { monto: formatMoney(efecto.valor), dias: dias(efecto.quedan) })}
          </p>
        )}
        {efecto && efecto.sinGanar > 0 && (
          <p className="text-xs text-amber-300" data-vacaciones-sin-ganar>
            {t('vacaciones.sinGanar', { ganados: dias(empleado.diasVacacionesAcumulados), sinGanar: dias(efecto.sinGanar) })}
          </p>
        )}
        <p className="text-xs text-slate-500">{t('vacaciones.sinDinero')}</p>
        <Campo etiqueta={t('comun.nota')} value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300} />
      </div>
    </Modal>
  );
}
